import React, { useState, useEffect } from 'react';
import { Target, X, Clock, TrendingUp, Calendar, Trophy, Award, DollarSign } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import axios from 'axios';

import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const AlertaObjetivosPendentes = ({ onNavigate }) => {
  const [objetivosPendentes, setObjetivosPendentes] = useState([]);
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    checkObjetivosPendentes();
    const interval = setInterval(checkObjetivosPendentes, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkObjetivosPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/objetivos-pontuais/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjetivosPendentes(response.data || []);
    } catch (error) {
      console.error('Erro ao verificar objetivos pendentes:', error);
    }
  };

  const getDiasRestantes = (dataLimite) => {
    if (!dataLimite) return 999;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const limite = new Date(dataLimite);
    const diffTime = limite - hoje;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getUrgencia = (diasRestantes) => {
    if (diasRestantes <= 1) return { color: 'bg-red-50 border-red-500', accent: 'bg-red-500', text: 'text-red-800', icon: '🔴' };
    if (diasRestantes <= 3) return { color: 'bg-orange-50 border-orange-500', accent: 'bg-orange-500', text: 'text-orange-800', icon: '🟠' };
    if (diasRestantes <= 7) return { color: 'bg-yellow-50 border-yellow-500', accent: 'bg-yellow-500', text: 'text-yellow-800', icon: '🟡' };
    return { color: 'bg-blue-50 border-blue-500', accent: 'bg-blue-500', text: 'text-blue-800', icon: '🎯' };
  };

  const formatValor = (v, tipoMeta, unidade) => {
    if (tipoMeta === 'quantidade') return `${v ?? 0}${unidade ? ' ' + unidade : ''}`;
    return `€${(v ?? 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatData = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (!showAlert || objetivosPendentes.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 space-y-3 animate-fade-in" data-testid="alerta-objetivos-pendentes">
      {objetivosPendentes.map((objetivo) => {
        const dataFim = objetivo.data_limite || objetivo.data_fim;
        const diasRestantes = getDiasRestantes(dataFim);
        const urgencia = getUrgencia(diasRestantes);
        const meta = Number(objetivo.meta_valor || 0);
        const progresso = Number(objetivo.progresso_atual || 0);
        const percentagem = meta > 0 ? Math.min(100, Math.round((progresso / meta) * 100 * 10) / 10) : 0;
        const faltam = Math.max(0, meta - progresso);
        const tipoMeta = objetivo.tipo_meta || 'valor';
        const unidade = objetivo.unidade_meta;
        const recompensa = objetivo.tipo_recompensa === 'dinheiro'
          ? `€${Number(objetivo.valor_recompensa || 0).toFixed(2)}`
          : `${objetivo.valor_recompensa || 0} pontos`;

        return (
          <Card key={objetivo.id} className={`border-l-4 ${urgencia.color}`} data-testid={`alerta-obj-${objetivo.id}`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`h-11 w-11 rounded-full ${urgencia.accent}/20 flex items-center justify-center flex-shrink-0 ${diasRestantes <= 1 ? 'animate-pulse' : ''}`}>
                    <Trophy className={`h-5 w-5 ${urgencia.text}`} />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    {/* Título + Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-semibold text-base sm:text-lg ${urgencia.text}`}>
                        {urgencia.icon} {objetivo.titulo || 'Objetivo Individual'}
                      </h3>
                      {diasRestantes <= 1 && (
                        <Badge className="bg-red-600 text-white animate-pulse">URGENTE</Badge>
                      )}
                      {objetivo.seccao && (
                        <Badge variant="outline" className="text-xs">{objetivo.seccao}</Badge>
                      )}
                    </div>

                    {/* Descrição (só se diferente do título) */}
                    {objetivo.descricao && objetivo.descricao !== objetivo.titulo && (
                      <p className="text-sm text-gray-700">{objetivo.descricao}</p>
                    )}

                    {/* Barra de progresso */}
                    {meta > 0 && (
                      <div className="pt-1">
                        <div className="flex items-baseline justify-between text-sm mb-1">
                          <span className="flex items-center gap-1 text-gray-700 font-medium">
                            <TrendingUp className="h-3.5 w-3.5" />
                            {formatValor(progresso, tipoMeta, unidade)} / {formatValor(meta, tipoMeta, unidade)}
                          </span>
                          <span className={`font-bold ${urgencia.text}`}>{percentagem}%</span>
                        </div>
                        <Progress value={percentagem} className="h-2.5" />
                        {faltam > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Faltam <strong>{formatValor(faltam, tipoMeta, unidade)}</strong> para atingir a meta
                          </div>
                        )}
                      </div>
                    )}

                    {/* Meta line grid — Período · Recompensa · Prazo */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs pt-1">
                      {objetivo.data_inicio && dataFim && (
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>
                            <strong>Período:</strong> {formatData(objetivo.data_inicio)} – {formatData(dataFim)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        {objetivo.tipo_recompensa === 'dinheiro' ? (
                          <DollarSign className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                        ) : (
                          <Award className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                        )}
                        <span className="text-gray-700">
                          <strong>Recompensa:</strong> {recompensa}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className={`h-3.5 w-3.5 flex-shrink-0 ${urgencia.text}`} />
                        <span className={`font-semibold ${urgencia.text}`}>
                          {diasRestantes < 0 ? 'Expirado' :
                           diasRestantes === 0 ? '⚠️ Termina hoje!' :
                           diasRestantes === 1 ? '⚠️ Termina amanhã!' :
                           `${diasRestantes} dias restantes`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => { onNavigate('objetivos'); setShowAlert(false); }}
                    className="bg-primary hover:bg-primary/90"
                    data-testid={`alerta-obj-ver-${objetivo.id}`}
                  >
                    Ver
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAlert(false)}
                    className="text-muted-foreground hover:bg-muted"
                    data-testid="alerta-obj-close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {objetivosPendentes.length > 1 && (
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            📋 Tens <strong>{objetivosPendentes.length} objetivos individuais</strong> por concluir
          </p>
        </div>
      )}
    </div>
  );
};

export default AlertaObjetivosPendentes;
